/**
 * LandingPage — `/` (design: "Landing" artboard, guest section)
 *
 * Hero ("Imtihonlar — besh minutda."), stats, exam-preview collage,
 * weekly leaderboard float, role feature cards.
 */
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Hex, Badge, Avatar } from '@/components/kit'
import {
  ArrowRight, Play, FileText, Trophy, Clock,
  Users, Pencil, Shield, BarChart3, BookOpen,
} from 'lucide-react'

// Mehmon (guest) sifatida kirish vaqtincha o'chirilgan — shu sababli
// "Mehmon uchun" kartasi ro'yxatdan olib tashlangan, kerak bo'lsa qaytariladi.
const FEATURES = [
  { t: 'Talaba uchun', d: "Imtihonlar ro'yxati, natijalar, reyting va o'qituvchilarni kuzatish.", tone: 'coral', icon: Users },
  { t: "O'qituvchi uchun", d: 'Savol bank, guruhlar, real-time monitoring, analitika.', tone: 'mint', icon: Pencil },
  { t: 'Admin uchun', d: 'Rollar, ruxsatlar, butun platforma boshqaruvi.', tone: 'lav', icon: Shield },
]

export default function LandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div style={{ color: 'var(--ink)' }}>

      {/* Hero */}
      <section className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center"
        style={{ padding: '48px 24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div>
          <Badge tone="coral" dot>BETA · 2026</Badge>
          <h1 className="h-display" style={{ fontSize: 'clamp(40px, 6vw, 64px)', lineHeight: 1.05, margin: '20px 0 16px', letterSpacing: '-0.03em' }}>
            {t('landing.heroLine1', 'Imtihonlar —')} <br />
            <span style={{ color: 'var(--coral)' }}>
              {t('landing.heroLine2', 'besh minutda')}<span style={{ color: 'var(--ink)' }}>.</span>
            </span>
          </h1>
          <p className="t-lg" style={{ color: 'var(--ink-2)', maxWidth: 480, lineHeight: 1.55 }}>
            {t('landing.heroSub', "O'qituvchilar yaratadigan imtihonlar, talabalar onlayn ishlaydigan platforma. Test, yozma, rasmli savol — bir joyda.")}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-primary" style={{ padding: '14px 22px', fontSize: 15, textDecoration: 'none' }}>
              {t('landing.ctaStart', 'Bepul boshlash')} <ArrowRight size={16} color="#fff" />
            </Link>
            <Link to="/exams" className="btn btn-outline" style={{ padding: '14px 22px', fontSize: 15, textDecoration: 'none' }}>
              <Play size={14} /> {t('landing.ctaExams', 'Ochiq imtihonlar')}
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 40 }}>
            {[
              ['12k+', t('landing.statStudents', 'faol talaba')],
              ['340+', t('landing.statTeachers', "o'qituvchi")],
              ['4.9★', t('landing.statRating', "o'rtacha baho")],
            ].map(([v, l], i) => (
              <div key={i} className="row" style={{ gap: 24 }}>
                {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />}
                <div>
                  <div className="h-display" style={{ fontSize: 28 }}>
                    {v.endsWith('★') ? <>{v.slice(0, -1)}<span style={{ fontSize: 18, color: 'var(--coral)' }}>★</span></> : v}
                  </div>
                  <div className="t-xs t-mut">{l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Visual collage */}
        <div className="hidden md:block" style={{ position: 'relative', height: 460 }}>
          {/* big card: exam preview */}
          <div className="card" style={{ position: 'absolute', top: 20, right: 0, width: 360, padding: 18, boxShadow: 'var(--shadow-lg)', borderRadius: 24 }}>
            <div className="row gap-12" style={{ marginBottom: 14 }}>
              <Hex size="sm" icon={FileText} bg="var(--coral-soft)" fg="var(--coral)" />
              <div style={{ flex: 1 }}>
                <div className="fw-7 t-sm">Algoritmlar · Final</div>
                <div className="t-xs t-mut">25 savol · 45 daqiqa</div>
              </div>
              <Badge tone="coral">Live</Badge>
            </div>
            <div style={{ padding: 12, background: 'var(--bg-soft)', borderRadius: 14, marginBottom: 10 }}>
              <div className="t-xs t-mut">12-savol / 25</div>
              <div className="fw-6 t-sm" style={{ marginTop: 4 }}>Big-O notatsiyasida quicksort?</div>
            </div>
            {["O(n log n) — o'rtacha", 'O(n²) — eng yomon', 'O(log n)', 'O(n)'].map((o, i) => (
              <div key={i} style={{
                padding: '10px 12px', marginBottom: 6, borderRadius: 12,
                border: i === 0 ? '1.5px solid var(--coral)' : '1px solid var(--border)',
                background: i === 0 ? 'var(--coral-tint)' : 'transparent',
                fontSize: 13, fontWeight: i === 0 ? 600 : 500,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{
                  width: 18, height: 18, borderRadius: '50%',
                  border: i === 0 ? '5px solid var(--coral)' : '2px solid var(--border-strong)',
                  background: i === 0 ? '#fff' : 'transparent',
                }} />
                {o}
              </div>
            ))}
            <div className="row" style={{ marginTop: 14, justifyContent: 'space-between' }}>
              <div className="row gap-8 t-xs t-mut"><Clock size={14} /> 28:14</div>
              <button onClick={() => navigate('/exams')} className="btn btn-primary" style={{ padding: '8px 14px', fontSize: 12 }}>
                Keyingisi <ArrowRight size={12} color="#fff" />
              </button>
            </div>
          </div>

          {/* small float: leaderboard */}
          <div className="card" style={{ position: 'absolute', bottom: 20, left: 0, width: 280, padding: 14, boxShadow: 'var(--shadow-md)', borderRadius: 20 }}>
            <div className="row gap-8" style={{ marginBottom: 10 }}>
              <Hex size="sm" icon={Trophy} bg="var(--butter-soft)" fg="var(--butter)" />
              <div className="fw-7 t-sm">{t('landing.weeklyRating', 'Hafta reytingi')}</div>
            </div>
            {[
              { n: 'Aziza K.', s: '1284', c: 'var(--coral)' },
              { n: 'Bekzod R.', s: '1156', c: 'var(--lav)' },
              { n: 'Madina T.', s: '1098', c: 'var(--mint)' },
            ].map((p, i) => (
              <div key={i} className="row gap-12" style={{ padding: '6px 0' }}>
                <span className="fw-7 t-sm" style={{ width: 16, color: 'var(--ink-3)' }}>#{i + 1}</span>
                <Avatar name={p.n} size={28} idx={i} />
                <span className="t-sm fw-6" style={{ flex: 1 }}>{p.n}</span>
                <span className="t-sm fw-7" style={{ color: p.c }}>{p.s}</span>
              </div>
            ))}
          </div>

          {/* hex constellation */}
          <div style={{ position: 'absolute', top: 0, left: 60, display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
            <Hex bg="var(--mint-soft)" fg="var(--mint)" icon={BarChart3} size="lg" />
            <Hex bg="var(--lav-soft)" fg="var(--lav)" icon={BookOpen} size="lg" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        style={{ padding: '20px 24px 60px', maxWidth: 1200, margin: '0 auto' }}>
        {FEATURES.map((f, i) => (
          <div key={i} className="card" style={{ padding: 22, borderRadius: 22 }}>
            <Hex size="md" icon={f.icon} bg={`var(--${f.tone}-soft)`} fg={`var(--${f.tone})`} />
            <div className="h-display" style={{ fontSize: 18, marginTop: 14 }}>{f.t}</div>
            <div className="t-sm t-mut" style={{ marginTop: 6, lineHeight: 1.5 }}>{f.d}</div>
          </div>
        ))}
      </section>
    </div>
  )
}
